/*
 * pdp-bundle-discount-lock.js
 * ---------------------------------------------------------------------------
 * Keeps the discounted pricing visible on EVERY Kaching Bundles bar, not just
 * the currently-selected one.
 *
 * WHY: the Kaching Bundles widget re-renders its bars on every selection
 * change and, depending on the app's config, only prints the compare-at
 * ("full price") strikethrough — and sometimes the discounted price itself —
 * on the bar that is selected. Switching options therefore strips the discount
 * off the bar you just left. We can't fix that from the app's CSS because the
 * strikethrough node simply isn't in the DOM for the unselected bars.
 *
 * HOW (pure DOM, no network, no render-blocking work):
 *   1. Stay dormant until the widget actually renders.
 *   2. The first time a bar shows its discounted state (a strikethrough that is
 *      higher than the live price), SNAPSHOT that bar's price + strikethrough.
 *   3. On any later re-render that drops the strikethrough or reverts the price
 *      to full, RE-ASSERT the snapshot. We only ever ADD back what Kaching
 *      removed — when the widget is already showing the discount we leave it
 *      untouched, so there is no fight and no observer loop.
 *   4. On bundle line items ("what's included") whose price is $0.00, drop the
 *      price and show "Included" instead. The real price is stashed on the node
 *      (data-mm-raw) so the snapshot logic in (2) still records the true value.
 *
 * This is deliberately read-mostly and self-contained, mirroring the approach
 * in snippets/pdp-bundle-howitworks.liquid. Loaded `defer` and gated to product
 * templates, so it has no effect on initial load or on any non-PDP page.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Config from the script tag (set in layout/theme.liquid from theme
  // settings + the page's product — nothing hard-coded here):
  //   data-mm-included-mode  'label' → free components read "Included";
  //                          'price' → keep Kaching's $0.00 + struck compare-at
  //   data-mm-bar-compare    'false' disables seeding a strikethrough on bars
  //   data-mm-variant-price  the product variant's real price in cents; used
  //                          to strike the regular price on single-product
  //                          bars Kaching renders without any markdown.
  var cfg = (function () {
    var s = document.currentScript;
    return {
      includedMode: (s && s.getAttribute('data-mm-included-mode')) === 'price' ? 'price' : 'label',
      seedBarCompare: !s || s.getAttribute('data-mm-bar-compare') !== 'false',
      variantPrice: s ? (parseInt(s.getAttribute('data-mm-variant-price'), 10) || 0) : 0
    };
  })();

  // Class hooks the Kaching Bundles widget uses (also the ones our theme CSS
  // in assets/kaching-bundles.css styles). Kept in one place so a widget markup
  // change is a one-line edit.
  var SEL = {
    widget: 'kaching-bundle, kaching-bundles-block',
    bar: '.kaching-bundles__bar',
    // class Kaching puts on the currently-selected bar
    selectedClass: 'kaching-bundles__bar--selected',
    pricing: '.kaching-bundles__bar-pricing',
    price: '.kaching-bundles__bar-price',
    full: '.kaching-bundles__bar-full-price',
    // bundle line items ("what's included")
    item: '.kaching-bundles__bundle-products__product',
    itemPricing: '.kaching-bundles__bundle-products__pricing',
    itemPrice: '.kaching-bundles__bundle-products__price',
    itemFull: '.kaching-bundles__bundle-products__full-price'
  };

  // Per-bar (and per-line-item) memory of the discounted display, keyed by a
  // stable index path so it survives Kaching tearing down and rebuilding nodes.
  var memory = Object.create(null);

  // Guard so our own writes don't re-trigger the observer into a loop.
  var applying = false;

  // Text shown in place of a $0.00 price on a bundle line item.
  var INCLUDED_LABEL = 'Included';

  // True when a money string represents zero (e.g. "$0.00", "0,00 €", "£0.00",
  // "¥0"). We look only at the digits: at least one, and all of them zero.
  // Strings with no digits ("Included", "Free") return false, so we never
  // clobber a label we (or Kaching) already set.
  function isFreeMoney(text) {
    if (!text) return false;
    var digits = String(text).replace(/\D/g, '');
    return digits.length > 0 && /^0+$/.test(digits);
  }

  // "$120.00" → 12000. Zero when unparseable, so callers can guard on > 0.
  function parseMoney(text) {
    var m = String(text || '').replace(/[^0-9.]/g, '');
    if (!m) return 0;
    return Math.round(parseFloat(m) * 100) || 0;
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // A row can end up with TWO strikethroughs after selection toggles: while a
  // bar is unselected Kaching drops its full-price node and we restore our own
  // copy; on re-select Kaching's renderer re-inserts its original node NEXT TO
  // ours (it diffs only its own children, so our foreign span survives).
  // Collapse to one node — prefer Kaching's own (no data-mm-restored), drop
  // ours. Kaching never resurrects our removed copy, so this converges.
  function dedupeFull(pricingEl, fullSelector) {
    if (!pricingEl) return null;
    var all = pricingEl.querySelectorAll(fullSelector);
    if (!all.length) return null;
    var keep = null;
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hasAttribute('data-mm-restored')) { keep = all[i]; break; }
    }
    if (!keep) keep = all[0];
    for (var j = 0; j < all.length; j++) {
      if (all[j] !== keep) all[j].parentNode.removeChild(all[j]);
    }
    return keep;
  }

  // Ensure exactly ONE strikethrough node exists in `pricingEl` with `text`,
  // creating it (with the widget's own class so theme CSS styles it) only
  // when missing.
  function ensureFull(pricingEl, fullSelector, fullClass, text) {
    if (!pricingEl || !text) return;
    var el = dedupeFull(pricingEl, fullSelector);
    if (!el) {
      el = document.createElement('span');
      el.className = fullClass;
      // Mark it as ours so we never mistake a restored node for a fresh
      // Kaching render when deciding whether to snapshot.
      el.setAttribute('data-mm-restored', '1');
      pricingEl.appendChild(el);
    }
    if (el.textContent !== text) el.textContent = text;
  }

  // Reconcile one pricing block (a bar, or a bundle line item).
  //
  // The SELECTED bar is the source of truth: while a bar is selected, Kaching
  // renders its real bundle pricing (e.g. the blade as $0.00 / free, the
  // trimmer as its bundled price). When a bar is NOT selected, Kaching re-renders
  // it with different/wrong numbers (the blade jumps to $18.00). So we CAPTURE
  // each row's prices while its bar is selected, and REPLAY that capture whenever
  // the bar is unselected — keeping the real, selected-state pricing on screen in
  // both states. Every value is read live from the widget; nothing is hard-coded.
  //
  //   key         - stable identity for the memory map
  //   pricingEl   - the container holding the price + (optional) strikethrough
  //   priceSel/fullSel/fullClass - selectors + class for this block's level
  //   isSelected  - whether the owning bar is currently selected
  function reconcile(key, pricingEl, priceSel, fullSel, fullClass, isSelected, allowIncluded) {
    if (!pricingEl) return;
    var priceEl = pricingEl.querySelector(priceSel);
    if (!priceEl) return;

    // Collapse duplicate strikethroughs FIRST, in every state — re-selecting
    // a bar re-inserts Kaching's own node next to our restored one, and the
    // capture below must never snapshot (or display) a doubled markdown.
    dedupeFull(pricingEl, fullSel);

    var snap;
    if (isSelected) {
      // Selected = canonical. Capture this row's current display verbatim.
      // If we've already relabelled a free row as "Included", recover the real
      // price we stashed on the node so we never capture the label as the price.
      var priceText = priceEl.textContent.trim();
      if (priceText === INCLUDED_LABEL && priceEl.getAttribute('data-mm-raw')) {
        priceText = priceEl.getAttribute('data-mm-raw');
      }
      var fullEl = pricingEl.querySelector(fullSel);
      var fullText = fullEl ? fullEl.textContent.trim() : '';
      if (priceText) memory[key] = { price: priceText, full: fullText };
      snap = memory[key];
    } else {
      // Unselected: replay the pricing captured from this row's selected state.
      snap = memory[key];
      if (snap) {
        if (priceEl.textContent !== snap.price) priceEl.textContent = snap.price;
        if (snap.full) ensureFull(pricingEl, fullSel, fullClass, snap.full);
      }
    }

    // Free line item → theme setting decides the display. 'label' drops the
    // price and says "Included"; 'price' keeps Kaching's own $0.00 + struck
    // compare-at. Driven off the captured RAW price (snap.price), so it's
    // reliable in either state and survives Kaching rebuilding the row. The
    // real price is stashed on the node so the selected-state capture above
    // still records the true value.
    if (allowIncluded && snap && isFreeMoney(snap.price)) {
      var strike = pricingEl.querySelector(fullSel);
      if (cfg.includedMode === 'price') {
        if (priceEl.textContent.trim() === INCLUDED_LABEL) {
          priceEl.textContent = snap.price;
        }
        if (strike && strike.style.display === 'none') strike.style.display = '';
      } else {
        if (priceEl.getAttribute('data-mm-raw') !== snap.price) {
          priceEl.setAttribute('data-mm-raw', snap.price);
        }
        if (priceEl.textContent.trim() !== INCLUDED_LABEL) {
          priceEl.textContent = INCLUDED_LABEL;
        }
        if (strike && strike.style.display !== 'none') strike.style.display = 'none';
      }
    }
  }

  function sweep() {
    var bars = document.querySelectorAll(SEL.bar);
    for (var b = 0; b < bars.length; b++) {
      var bar = bars[b];
      var isSelected = bar.classList.contains(SEL.selectedClass);
      // Bar-level pricing. Never relabelled — a bar total is not a "free item".
      reconcile(
        'bar:' + b,
        bar.querySelector(SEL.pricing),
        SEL.price, SEL.full,
        'kaching-bundles__bar-full-price',
        isSelected,
        false
      );
      // Markdown on single-product bars: Kaching renders the one-time bar
      // with just the deal price ($120.00) and no strikethrough. When the
      // deal price is below the variant's real price (passed in from Liquid),
      // strike that real price next to it — same node/class Kaching uses, so
      // the theme CSS styles it identically. Scoped to bars WITHOUT a
      // bundle-products list: multi-product bars get their full price from
      // Kaching itself, and seeding them early would flash a wrong number.
      if (cfg.seedBarCompare && cfg.variantPrice > 0 &&
          !bar.querySelector('.kaching-bundles__bundle-products')) {
        var barPricing = bar.querySelector(SEL.pricing);
        var barPrice = barPricing && barPricing.querySelector(SEL.price);
        if (barPrice && !barPricing.querySelector(SEL.full)) {
          var liveCents = parseMoney(barPrice.textContent);
          if (liveCents > 0 && liveCents < cfg.variantPrice) {
            ensureFull(barPricing, SEL.full, 'kaching-bundles__bar-full-price', formatMoney(cfg.variantPrice));
          }
        }
      }

      // Bundle line items ("what's included"). Free ones become "Included".
      var items = bar.querySelectorAll(SEL.item);
      for (var i = 0; i < items.length; i++) {
        reconcile(
          'bar:' + b + ':item:' + i,
          items[i].querySelector(SEL.itemPricing),
          SEL.itemPrice, SEL.itemFull,
          'kaching-bundles__bundle-products__full-price',
          isSelected,
          true
        );
      }
    }
  }

  var rafPending = false;
  function schedule() {
    if (rafPending || applying) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      applying = true;
      try { sweep(); } finally { applying = false; }
    });
  }

  function init() {
    // The Kaching widget isn't reliably wrapped in a <kaching-bundle> custom
    // element (in this theme the bars render in plain markup), and it tears
    // down / rebuilds its bars on every selection change. So we don't try to
    // "lock onto" a single widget node — we watch the document and re-sweep on
    // any change, debounced to once per frame. sweep() early-exits when there
    // are no bars, so the cost on non-widget mutations is a couple of empty
    // querySelectorAll calls. This mirrors snippets/pdp-bundle-howitworks.liquid,
    // which observes document.body the same way.
    schedule(); // initial snapshot/restore (widget may already be rendered)
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        // `class` so a selection change (bar--selected toggling) re-sweeps even
        // if Kaching doesn't also re-render the price text. Coalesced to one
        // sweep per frame by the rAF debounce in schedule().
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
